import { Box } from '@mui/material';
import { useAddExerciseToPatient } from '_api';
import { AddExerciseToPatientPayload } from '_api/types';
import { SEVERITY } from '_constants/derived';
import { PrimaryButton, SecondaryButton } from 'components/atoms';
import { AlertMessage, BaseModal } from 'components/molecules';
import { AddExerciseToPatientProfileForm } from 'components/organisms/forms';
import {
  hasNonblankPatientInstructions,
  normalizePatientInstructions,
} from 'components/organisms/forms/patientInstructions';
import React, { useState } from 'react';
import { useExercise } from 'utils/hooks';

import { buildExercisePayload } from './buildExercisePayload';
import { AddToPatientProfileModalProps } from './types';

export const AddExerciseToPatientProfileModal = ({
  open,
  onClose,
  patientId,
}: AddToPatientProfileModalProps) => {
  const { availableExercises, getExerciseById } = useExercise();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { mutateAsync: addExerciseToPatient } = useAddExerciseToPatient();
  const [payload, setPayload] = useState<AddExerciseToPatientPayload[]>([]);

  const close = () => {
    onClose();
    setPayload([]);
  };

  const updateList = async (ids: string[]) => {
    setPayload((current) => {
      const existingIds = new Set(current.map((item) => item.contentId));
      const newExercises = ids
        .filter((id) => !existingIds.has(Number(id)))
        .map((id) => {
          const exercise = availableExercises?.find((ex) => ex.id === Number(id));
          return buildExercisePayload(Number(id), patientId, exercise);
        });
      const filteredPayload = current.filter((item) =>
        ids.includes(item.contentId.toString()),
      );
      return [...filteredPayload, ...newExercises];
    });
  };

  const updateExercise = async (key: string, value: any, id: number) => {
    setPayload((current) =>
      current.map((exc) => (exc.contentId === id ? { ...exc, [key]: value } : exc)),
    );
  };

  const checkError = (
    errorExercise: AddExerciseToPatientPayload | undefined,
    message: string,
  ) => {
    if (errorExercise) {
      const exercise = getExerciseById(Number(errorExercise.contentId));
      if (exercise) {
        setErrorMessage(`Exercise: '${exercise.title} ${message}`);
      }
      return true;
    }
    return false;
  };

  const findErrors = (payload: AddExerciseToPatientPayload[]) => {
    const errorEx = payload.find((ex) => ex.regimenType === '');
    const perWeekError = payload.find((ex) => ex.perWeek <= 0);
    const perDayError = payload.find((ex) => ex.perDay <= 0);
    const fatigueError = payload.find(
      (ex) => ex.regimenType === 'FATIGUE' && ex.sets <= 0,
    );
    const repetitionError = payload.find(
      (ex) => ex.regimenType === 'REPETITION' && (ex.sets <= 0 || ex.reps <= 0),
    );
    const therapyTypeError = payload.find((ex) => ex.therapyType === undefined);
    const instructionError = payload.find(
      (ex) => !hasNonblankPatientInstructions(ex.instructionList),
    );
    return (
      checkError(errorEx, 'must have an EXERCISE DETAIL selected.') ||
      checkError(perWeekError, 'must have a TIMES PER WEEK value greater than 0.') ||
      checkError(perDayError, 'must have a TIMES PER DAY value greater than 0.') ||
      checkError(fatigueError, 'must have a SETS value greater than 0.') ||
      checkError(
        repetitionError,
        'must have a SETS and REPETITIONS values greater than 0.',
      ) ||
      checkError(therapyTypeError, 'must have a THERAPY TYPE selected.') ||
      checkError(instructionError, 'must have at least one instruction.')
    );
  };

  const onSubmit = () => {
    if (payload.length === 0) {
      setErrorMessage('Please select exercise(s) to add to the patient.');
    } else if (!findErrors(payload)) {
      addExerciseToPatient(
        payload.map((exercise) => ({
          ...exercise,
          instructionList: normalizePatientInstructions(exercise.instructionList),
        })) as AddExerciseToPatientPayload[],
      )
        .then(() => {
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
            close();
          }, 1500);
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            setErrorMessage(
              'Unexpected error occured. Please close the modal and try again.',
            );
          } else if (error.message) {
            setErrorMessage(`ERROR: ${error.message}`);
          } else {
            setErrorMessage('Unexpected error. Please Try again.');
          }
        });
    }
  };

  const Footer = () => {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          padding: 5,
          paddingBottom: 8,
          justifyContent: 'flex-end',
          gap: 2,
          mt: 2,
        }}
      >
        <SecondaryButton variant="outlined" color="primary" onClick={close}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={onSubmit}>Save</PrimaryButton>
      </Box>
    );
  };

  return (
    <BaseModal open={open} onClose={close} title={'Add Exercise'} footer={Footer}>
      <AddExerciseToPatientProfileForm
        patientId={patientId}
        exerciseList={payload}
        onBack={close}
        onSubmit={onSubmit}
        updateListOfObjects={updateList}
        updateObject={updateExercise}
      />
      <AlertMessage
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={'Successfully added exercise to patient profile'}
      />
      <AlertMessage
        open={!!errorMessage && errorMessage.length > 0}
        onClose={() => setErrorMessage('')}
        severity={SEVERITY.error}
        message={
          <>
            Failed to add exercise to the patient profile.
            <br />
            {errorMessage}
            <br />
            Resolve form issues and try again.
          </>
        }
      />
    </BaseModal>
  );
};
